import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Layout,
    Row,
    Col,
    Card,
    Tabs,
    Input,
    Button,
    Space,
    Switch,
    Progress,
    Tag,
    Divider,
    Avatar,
    Tooltip,
    Typography,
    Upload,
    Segmented,
    Badge,
    DatePicker,
    message,
    notification,
    Popconfirm,
    Popover,
    Select
} from "antd";
import {
    SendOutlined,
    ThunderboltOutlined,
    EyeOutlined,
    CalendarOutlined,
    ClockCircleOutlined,
    InstagramOutlined,
    LinkedinOutlined,
    FacebookOutlined,
    PictureOutlined,
    VideoCameraOutlined,
    ScissorOutlined,
    PlusOutlined,
    MessageOutlined,
    UserAddOutlined,
    CommentOutlined
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { contentPlanActions, postActions, questionsActions } from "../../store";
import moment from "moment";
import { fetchWrapper } from "../../helpers";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { daysOfWeek } from "../../constants/daysOfWeek";
import {ImeanHeader} from "../../components/Header/Header";

const { Header, Footer, Content } = Layout;
const { Title, Text } = Typography;
const { TabPane } = Tabs;

// Constants
const baseUrl = `${process.env.REACT_APP_API_URL}`;
const PER_IMAGE_LIMIT = 8 * 1024 * 1024; // 8 MB per image
const IMAGES_TOTAL_LIMIT = 80 * 1024 * 1024; // 80 MB total
const COMBINED_WARN_LIMIT = 10 * 1000 * 1000; // 10 MB warning
const MAX_GENERATE_IMAGE_TRIES = 3;

// Custom hooks
const useNotify = () => {
    const [api, context] = notification.useNotification();
    return { api, context };
};

const useSnTokens = () => {
    const [tokens, setTokens] = useState({ facebook: false, instagram: false, linkedin: false });
    useEffect(() => {
        fetchWrapper
            .get(`${baseUrl}/auth/getSocialMediaTokens/`)
            .then((res) => {
                const next = { facebook: false, instagram: false, linkedin: false };
                res.forEach((t) => {
                    if (t.socialMediaType === "FACEBOOK") next.facebook = true;
                    if (t.socialMediaType === "INSTAGRAM") next.instagram = true;
                    if (t.socialMediaType === "LINKEDIN") next.linkedin = true;
                });
                setTokens(next);
            })
            .catch(() => {});
    }, []);
    return tokens;
};

const HashOutlined = (props) => (
    <svg
        viewBox="0 0 1024 1024"
        fill="currentColor"
        height="1em"
        width="1em"
        {...props}
    >
        <path d="M912 384H726l28-160h158V160H768l24-128H656l-24 128H392l24-128H288l-24 128H112v64h140l-28 160H64v64h148l-28 160H32v64h140l-24 128h88l24-128h240l-24 128h88l24-128h152v-64H726l28-160h158v-64zm-372 160H388l28-160h152l-28 160z" />
    </svg>
);

// PURELY PRESENTATIONAL PREVIEW — NO BUSINESS LOGIC
// Tailwind not required. Uses Ant Design only.

export default function PostComposerAntDPreview() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { contentId } = useParams();

    // Store slices
    const user = useSelector((s) => s.auth.user);
    const post = useSelector((s) => s.post.post);
    const { loading: postLoading, generating: postGenerating } = useSelector((s) => s.post);
    const contentPlan = useSelector((s) => s.contentPlan.contentPlan);

    // Local state
    const videoPlayerRef = useRef(null);
    const hiddenVideoInput = useRef(null);
    const hiddenFileInput = useRef(null);

    const [images, setImages] = useState([]);
    const [video, setVideo] = useState(null);
    const [generating, setGenerating] = useState(false);

    const [editMainText, setEditMainText] = useState(false);
    const [editHashtag, setEditHashtag] = useState(false);
    const [editOffer, setEditOffer] = useState(false);

    const [mainText, setMainText] = useState(post?.post || "");
    const [hashtags, setHashtags] = useState(post?.hashtags || "");
    const [offer, setOffer] = useState(post?.media || "");

    const [publishTime, setPublishTime] = useState(contentPlan?.date || new Date().toISOString());
    const [weekDay, setWeekDay] = useState(contentPlan?.weekDay || "");

    const [dragActive, setDragActive] = useState(false);
    const snTokens = useSnTokens();
    const [snForAnotherPost, setSnForAnotherPost] = useState([]);
    const [selectedPlatforms, setSelectedPlatforms] = useState(["linkedin", "facebook", "instagram"]);
    const [activePreview, setActivePreview] = useState("linkedin");

    const { api: notify, context: notificationContextHolder } = useNotify();

    // Effects
    useEffect(() => {
        if (post) {
            setMainText(post.post || "");
            setHashtags(post.hashtags || "");
            setOffer(post.media || "");
        }
    }, [post]);

    useEffect(() => {
        const initial = [];
        if (contentPlan?.socialNetwork?.includes("LinkedIn")) initial.push("LinkedIn");
        if (contentPlan?.socialNetwork?.includes("Facebook")) initial.push("Facebook");
        if (contentPlan?.socialNetwork?.includes("Instagram")) initial.push("Instagram");
        setSnForAnotherPost(initial);
    }, [contentPlan]);

    useEffect(() => {
        if (contentPlan?.socialNetwork?.toLowerCase().includes("facebook")) {
            fetchWrapper.get(`${baseUrl}/profile/`).then((res) => {
                if (!res.facebookPageId) openNotificationFbPages();
            });
        }
        if (contentPlan?.socialNetwork?.toLowerCase().includes("instagram") && (!post?.images?.length && !post?.video)) {
            message.info(t("functions.minMediaForInstagramPostMessage"));
        }
    }, [contentPlan, post]);

    useEffect(() => {
        const total = [...images, video].reduce((acc, m) => acc + (m?.size || 0), 0);
        if (total > COMBINED_WARN_LIMIT) message.error(t("post.bigMediaListSize", { size: 64 }));
    }, [images, video]);

    // Notifications
    const openNotificationSocialNetwork = ({ message: msg, description }) => {
        const key = `open${Date.now()}`;
        const btn = (
            <Space>
                <Button type="primary" size="small" onClick={() => navigate("/profile")}>
                    {t("posts.goToProfile")}
                </Button>
            </Space>
        );
        notify.open({ message: msg || t("functions.error"), description: description || t("posts.unknownError"), btn, key });
    };

    const openNotificationFbPages = () => {
        const key = `open${Date.now()}`;
        const btn = (
            <Space>
                <Button type="primary" size="small" onClick={() => navigate("/profile?helpFbModal=true")}>
                    {t("posts.goToProfile")}
                </Button>
            </Space>
        );
        notify.open({ message: t("functions.warning"), description: t("posts.notSelectedFacebookPage"), btn, key });
    };

    // Helpers
    const clipboardCopy = (text) => {
        navigator.clipboard.writeText(text);
        message.success(t("functions.copied"));
    };

    const enforceTextLimits = () => {
        const textToCheck = editMainText ? mainText : post?.post;
        if (!textToCheck) return true;

        const len = textToCheck.length;
        let ok = true;
        let badNet = null;

        selectedPlatforms.forEach((platform) => {
            if (platform === "instagram" && len > 2200) { badNet = "Instagram"; ok = false; }
            if (platform === "linkedin" && len > 3000) { badNet = "LinkedIn"; ok = false; }
            if (platform === "facebook" && len > 8000) { badNet = "Facebook"; ok = false; }
        });

        if (!ok) {
            const key = `char-limit-${Date.now()}`;
            const limit = badNet === "Instagram" ? 2200 : badNet === "LinkedIn" ? 3000 : 8000;
            notify.warning({
                message: t("functions.warning"),
                description: t("functions.characterLimitError", { network: badNet, limit }),
                key,
                duration: 0,
            });
        }
        return ok;
    };

    const requireInstagramMedia = () => {
        if (selectedPlatforms.includes("instagram") && (!post?.images?.length && !post?.video && !images.length && !video)) {
            message.warning(t("functions.minMediaForInstagramPostMessage"));
            return false;
        }
        return true;
    };

    // Change handlers
    const changeMainText = async () => {
        if (mainText.length > 8000) return message.error(t("functions.maxTextLength", { limit: 8000 }));
        if (!mainText.trim()) return message.error(t("functions.noText"));
        if (mainText.trim().length < 25) return message.error(t("functions.minTextLength", { limit: 25 }));

        if (editMainText && post?.id) {
            try {
                const res = await fetchWrapper.put(`${baseUrl}/post/update/${post.id}`, {
                    post: mainText, hashtags, media: offer
                });
                if (res) {
                    dispatch(postActions.updatePost({
                        postId: post.id,
                        contentPlanId: contentPlan?.id,
                        postData: { post: mainText, hashtags, media: offer }
                    }));
                    message.success(t("functions.changesSaved"));
                }
            } catch (e) {
                return message.error(t("functions.wrong"));
            }
        }
        setEditMainText((v) => !v);
    };

    const changeHashtags = async () => {
        if (editHashtag && post?.id) {
            try {
                const res = await fetchWrapper.put(`${baseUrl}/post/update/${post.id}`, {
                    post: mainText, hashtags, media: offer
                });
                if (res) {
                    dispatch(postActions.updatePost({
                        postId: post.id,
                        contentPlanId: contentPlan?.id,
                        postData: { post: mainText, hashtags, media: offer }
                    }));
                    message.success(t("functions.changesSaved"));
                }
            } catch (e) {
                return message.error(t("functions.wrong"));
            }
        }
        setEditHashtag((v) => !v);
    };

    const changeOffer = async () => {
        if (editOffer && post?.id) {
            try {
                const res = await fetchWrapper.put(`${baseUrl}/post/update/${post.id}`, {
                    post: mainText, hashtags, media: offer
                });
                if (res) {
                    dispatch(postActions.updatePost({
                        postId: post.id,
                        contentPlanId: contentPlan?.id,
                        postData: { post: mainText, hashtags, media: offer }
                    }));
                    message.success(t("functions.changesSaved"));
                }
            } catch (e) {
                return message.error(t("functions.wrong"));
            }
        }
        setEditOffer((v) => !v);
    };

    const changePublishTime = (iso) => {
        setPublishTime(iso);
        if (post?.id) {
            fetchWrapper.put(`${baseUrl}/post/update/${post.id}`, { date: iso });
        }
    };

    const changeWeekDay = (value) => {
        if (post?.id) {
            fetchWrapper.put(`${baseUrl}/post/update/${post.id}`, { weekday: value })
                .then((res) => { if (res) setWeekDay(value); });
        } else {
            setWeekDay(value);
        }
    };

    // Media handlers
    const pushImagesWithinTotal = (prevImages, newImages) => {
        const merged = [...prevImages, ...newImages];
        const total = merged.reduce((s, f) => s + (f?.size || 0), 0);
        if (total <= IMAGES_TOTAL_LIMIT) return merged.slice(0, 6);

        let acc = prevImages.reduce((s, f) => s + (f?.size || 0), 0);
        const accepted = [...prevImages];
        for (const f of newImages) {
            const next = acc + (f?.size || 0);
            if (next <= IMAGES_TOTAL_LIMIT) {
                accepted.push(f);
                acc = next;
            } else {
                message.error(t("functions.totalImagesTooLarge") || "Total images size must be under 80MB");
            }
        }
        return accepted.slice(0, 6);
    };

    const handleImageFilePicker = (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;

        const validImages = files.filter(file => {
            if (!file.type.includes("image")) {
                message.error(`${file.name} is not a valid image file`);
                return false;
            }
            if (file.size > PER_IMAGE_LIMIT) {
                message.error(`${file.name} is too large. Maximum size is 8MB per image`);
                return false;
            }
            return true;
        });

        setImages((prev) => pushImagesWithinTotal(prev, validImages));
    };

    const handleRemoveVideo = async () => {
        setVideo(null);
        if (post?.video && post?.id) {
            try {
                await dispatch(postActions.removeVideo({
                    postId: post.id,
                    contentPlanId: contentPlan?.id,
                    video
                }));
                window.location.reload();
            } catch (err) {
                message.error(t("functions.wrong"));
            }
        }
    };

    // Drag & Drop
    const handleDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); };
    const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); };
    const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
    const handleDrop = (e) => {
        e.preventDefault(); e.stopPropagation(); setDragActive(false);
        const list = Array.from(e.dataTransfer.files || []);
        if (!list.length) return;

        const newImages = [];
        list.forEach((file) => {
            if (file.type.includes("image")) {
                if (file.size <= PER_IMAGE_LIMIT) newImages.push(file);
                else message.error(t("functions.imageTooLarge") || "Each image must be under 8MB");
            }
            if (file.type.includes("video")) {
                message.info(t("functions.error")); // Video blocked
            }
        });

        setImages((prev) => pushImagesWithinTotal(prev, newImages));
    };

    const handleMagicAssist = () => {
        if (!mainText.trim()) {
            message.warning("Please enter some content first for AI assistance");
            return;
        }
        message.info("AI assistance feature would enhance your post content");
    };

    const handleSuggestHashtags = () => {
        if (!mainText.trim()) {
            message.warning("Please enter post content first to suggest relevant hashtags");
            return;
        }
        // Simulate hashtag suggestions based on content
        const suggestions = ["#marketing", "#socialmedia", "#engagement", "#content", "#digital"];
        const newHashtags = hashtags ? `${hashtags} ${suggestions.join(" ")}` : suggestions.join(" ");
        setHashtags(newHashtags);
        message.success("Hashtag suggestions added!");
    };

    const persistTextIfEditing = async () => {
        if (!editMainText || !post?.id) return true;
        try {
            const res = await fetchWrapper.put(`${baseUrl}/post/update/${post.id}`, {
                post: mainText, hashtags, media: offer
            });
            if (res) {
                dispatch(postActions.updatePost({
                    postId: post.id,
                    contentPlanId: contentPlan?.id,
                    postData: { post: mainText, hashtags, media: offer }
                }));
                setEditMainText(false);
                return true;
            }
        } catch (e) {
            message.error(t("functions.wrong"));
        }
        return false;
    };

    const onPublish = async () => {
        if (!enforceTextLimits()) return;
        if (!requireInstagramMedia()) return;

        if (!(await persistTextIfEditing())) return;

        // Upload images if present
        if (images.length && post?.id && contentPlan?.id) {
            try {
                await dispatch(postActions.uploadImage({
                    postId: post.id,
                    contentPlanId: contentPlan.id,
                    images
                }));
                message.success("Post published successfully!");
                navigate("/posts");
            } catch {
                message.error(t("functions.wrong"));
            }
            return;
        }

        message.success("Post published successfully!");
        navigate("/posts");
    };

    const onSchedule = () => {
        message.success(`Post scheduled for ${moment(publishTime).format("MMM DD, YYYY at HH:mm")}`);
        navigate("/posts");
    };

    const onSaveDraft = async () => {
        if (post?.id) {
            await persistTextIfEditing();
        }
        message.success("Draft saved successfully!");
    };
    return (
        <Layout style={{ minHeight: "100vh", background: "linear-gradient(180deg, #ffffff, #faf5ff)" }}>
            {/* Top Bar */}
            <ImeanHeader initialDark={true}></ImeanHeader>
            <Header style={{ position: "sticky", top: 0, zIndex: 10, background: "rgba(255,255,255,0.8)", backdropFilter: "blur(6px)", borderBottom: "1px solid #f0f0f0" }}>
                <Row align="middle" justify="space-between">
                    <Space align="center" size={12}>
                        <Badge color="#7c3aed" />
                        <Title level={4} style={{ margin: 0 }}>Compose Post</Title>
                    </Space>
                    <Space>
                        <Button icon={<ThunderboltOutlined />} type="default">Magic Assist</Button>
                        <Button icon={<SendOutlined />} type="primary">Publish</Button>
                    </Space>
                </Row>
            </Header>

            <Content style={{ maxWidth: 1200, margin: "24px auto", width: "100%", padding: "0 16px" }}>
                <Row gutter={[16, 16]}>
                    {/* LEFT COLUMN */}
                    <Col xs={24} lg={14}>
                        {/* Target & Schedule */}
                        <Card bordered className="shadow-sm" bodyStyle={{ padding: 16 }}>
                            <Row align="middle" justify="space-between">
                                <Space size={12} align="center">
                                    <Title level={5} style={{ margin: 0 }}>Target & Schedule</Title>
                                </Space>
                                <Space split={<Divider type="vertical" />} style={{ color: "#8c8c8c" }}>
                                    <Space><InstagramOutlined />Instagram</Space>
                                    <Space><LinkedinOutlined />LinkedIn</Space>
                                    <Space><FacebookOutlined />Facebook</Space>
                                    <Space><CalendarOutlined />Today, 15:30</Space>
                                    <Space><ClockCircleOutlined />Best: 18:00–20:00</Space>
                                </Space>
                            </Row>
                            <Divider style={{ margin: "12px 0" }} />
                            <Row gutter={[12, 12]}>
                                <Col xs={24} md={12}>
                                    <Card size="small" bordered>
                                        <Space direction="vertical" style={{ width: "100%" }}>
                                            <Text strong>Posting Windows</Text>
                                            <Row gutter={[7, 7]}>
                                                {"Mo Tu We Th Fr Sa Su".split(" ").map((d, i) => (
                                                    <Col key={i}><Tag>{d}</Tag></Col>
                                                ))}
                                            </Row>
                                            <Segmented options={["Morning", "Afternoon", "Evening"]} block />
                                        </Space>
                                    </Card>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Card size="small" bordered>
                                        <Space direction="vertical" style={{ width: "100%" }}>
                                            <Text strong>Auto-Optimization</Text>
                                            <Row justify="space-between" align="middle">
                                                <Text>Best-time recommendations</Text>
                                                <Switch defaultChecked />
                                            </Row>
                                            <Row justify="space-between" align="middle">
                                                <Text>Auto-reschedule on conflict</Text>
                                                <Switch defaultChecked />
                                            </Row>
                                        </Space>
                                    </Card>
                                </Col>
                            </Row>
                        </Card>

                        {/* Post Content */}
                        <Card className="shadow-sm" style={{ marginTop: 16 }} bodyStyle={{ padding: 16 }}>
                            <Row justify="space-between" align="middle">
                                <Title level={5} style={{ margin: 0 }}>Post Content</Title>
                                <Space>
                                    <Button icon={<ThunderboltOutlined />}>Improve</Button>
                                    <Button icon={<ThunderboltOutlined />}>Rewrite</Button>
                                </Space>
                            </Row>
                            <Divider style={{ margin: "12px 0" }} />
                            <Input.TextArea placeholder="Write something engaging…" autoSize={{ minRows: 8 }} />
                            <Row justify="space-between" align="middle" style={{ marginTop: 12 }}>
                                <Space><EyeOutlined /> Estimated reach: 12–18k</Space>
                                <Space><Layout /> Reading time: ~45s</Space>
                            </Row>
                            <div style={{ marginTop: 12 }}>
                                <Text type="secondary" style={{ fontSize: 12 }}>Content Score</Text>
                                <Progress percent={78} showInfo={false} strokeColor="#7c3aed" style={{ marginTop: 6 }} />
                                <Text type="secondary" style={{ fontSize: 12 }}>Clarity, tone, and structure look good. Try adding a question at the end.</Text>
                            </div>
                        </Card>

                        {/* Hashtags */}
                        <Card className="shadow-sm" style={{ marginTop: 16 }} bodyStyle={{ padding: 16 }}>
                            <Row justify="space-between" align="middle">
                                <Title level={5} style={{ margin: 0 }}>Hashtags</Title>
                                <Button icon={<HashOutlined />}>Suggest</Button>
                            </Row>
                            <Divider style={{ margin: "12px 0" }} />
                            <Input placeholder="#marketing #b2b #casestudy" />
                            <Space wrap style={{ marginTop: 12 }}>
                                {["#growth", "#leadership", "#smb", "#product", "#ai"].map((tag) => (
                                    <Tag key={tag} color="purple">{tag}</Tag>
                                ))}
                            </Space>
                        </Card>

                        {/* Media */}
                        <Card className="shadow-sm" style={{ marginTop: 16 }} bodyStyle={{ padding: 16 }}>
                            <Row justify="space-between" align="middle">
                                <Title level={5} style={{ margin: 0 }}>Media</Title>
                                <Space>
                                    <Button icon={<PictureOutlined />}>Upload</Button>
                                    <Button icon={<VideoCameraOutlined />}>Add Video</Button>
                                    <Button icon={<ThunderboltOutlined />}>Generate</Button>
                                </Space>
                            </Row>
                            <Divider style={{ margin: "12px 0" }} />
                            <Tabs defaultActiveKey="grid">
                                <TabPane tab="Grid" key="grid">
                                    <Row gutter={[8, 8]}>
                                        {Array.from({ length: 6 }).map((_, i) => (
                                            <Col xs={8} key={i}>
                                                <div style={{ aspectRatio: "1 / 1", borderRadius: 12, background: "#f7f7f7", border: "1px solid #f0f0f0" }} />
                                            </Col>
                                        ))}
                                    </Row>
                                </TabPane>
                                <TabPane tab="Crop & Resize" key="crop">
                                    <Row gutter={[8, 8]}>
                                        {["1:1", "16:9", "9:16"].map((r) => (
                                            <Col xs={8} key={r}>
                                                <Card size="small" bordered className="center">
                                                    <Space><ScissorOutlined /> {r}</Space>
                                                </Card>
                                            </Col>
                                        ))}
                                    </Row>
                                </TabPane>
                                <TabPane tab="Brand Overlay" key="brand">
                                    <Row gutter={[12, 12]}>
                                        <Col xs={24} md={12}>
                                            <Card size="small" bordered>Logo position</Card>
                                        </Col>
                                        <Col xs={24} md={12}>
                                            <Card size="small" bordered>Watermark & CTA</Card>
                                        </Col>
                                    </Row>
                                </TabPane>
                            </Tabs>
                        </Card>
                    </Col>

                    {/* RIGHT COLUMN */}
                    <Col xs={24} lg={10}>
                        {/* Live Preview */}
                        <Card className="shadow-sm" bodyStyle={{ padding: 16 }}>
                            <Space align="center" size={8} style={{ marginBottom: 8 }}>
                                <EyeOutlined />
                                <Title level={5} style={{ margin: 0 }}>Live Preview</Title>
                            </Space>
                            <Tabs defaultActiveKey="linkedin">
                                <TabPane tab={<Space><LinkedinOutlined />LinkedIn</Space>} key="linkedin">
                                    <Card size="small" bordered>
                                        <Space align="center" size={8} style={{ marginBottom: 12 }}>
                                            <Avatar size={32} style={{ background: "#1677ff" }}>IN</Avatar>
                                            <Space direction="vertical" size={0}>
                                                <Text strong>Company Page</Text>
                                                <Text type="secondary" style={{ fontSize: 12 }}>Now</Text>
                                            </Space>
                                        </Space>
                                        <Space direction="vertical" style={{ width: "100%" }}>
                                            <div style={{ height: 8, width: "100%", background: "#f0f0f0", borderRadius: 6 }} />
                                            <div style={{ height: 8, width: "85%", background: "#f0f0f0", borderRadius: 6 }} />
                                            <div style={{ height: 8, width: "65%", background: "#f0f0f0", borderRadius: 6 }} />
                                        </Space>
                                        <Row gutter={[8, 8]} style={{ marginTop: 12 }}>
                                            {Array.from({ length: 3 }).map((_, i) => (
                                                <Col span={8} key={i}>
                                                    <div style={{ aspectRatio: "16/9", background: "#f0f0f0", borderRadius: 8 }} />
                                                </Col>
                                            ))}
                                        </Row>
                                    </Card>
                                </TabPane>
                                <TabPane tab={<Space><InstagramOutlined />Instagram</Space>} key="instagram">
                                    <Card size="small" bordered>
                                        <div style={{ aspectRatio: "1/1", width: "100%", background: "#f0f0f0", borderRadius: 12 }} />
                                        <div style={{ height: 8, width: "60%", background: "#f0f0f0", borderRadius: 6, marginTop: 12 }} />
                                        <div style={{ height: 8, width: "40%", background: "#f0f0f0", borderRadius: 6, marginTop: 8 }} />
                                    </Card>
                                </TabPane>
                                <TabPane tab={<Space><FacebookOutlined />Facebook</Space>} key="facebook">
                                    <Card size="small" bordered>
                                        <Space align="center" size={8} style={{ marginBottom: 12 }}>
                                            <Avatar size={32} style={{ background: "#1677ff" }}>FB</Avatar>
                                            <Space direction="vertical" size={0}>
                                                <Text strong>Business Page</Text>
                                                <Text type="secondary" style={{ fontSize: 12 }}>Now</Text>
                                            </Space>
                                        </Space>
                                        <div style={{ height: 8, width: "100%", background: "#f0f0f0", borderRadius: 6 }} />
                                        <div style={{ height: 8, width: "80%", background: "#f0f0f0", borderRadius: 6, marginTop: 8 }} />
                                        <div style={{ aspectRatio: "16/9", width: "100%", background: "#f0f0f0", borderRadius: 8, marginTop: 12 }} />
                                    </Card>
                                </TabPane>
                            </Tabs>
                        </Card>

                        {/* Preview for the new content plan and instant post card */}
                        <Card className="shadow-sm" style={{ marginTop: 16 }} bodyStyle={{ padding: 16 }}>
                            <Space align="center" size={8} style={{ marginBottom: 8 }}>
                                <EyeOutlined />
                                <Typography.Title level={5} style={{ margin: 0 }}>Post Title</Typography.Title>
                            </Space>
                            <Card size="small" bordered className={"border-purple-500 cursor-pointer post-item p-2 rounded-lg border-l-4 border-t-0 border-r-0 border-b-0 shadow-sm transition-transform hover:scale-[1.05]"}>
                                <Space align="center" size={8} style={{ marginBottom: 12 }}>
                                    <Avatar size={32} style={{ background: "#1677ff" }}>IN</Avatar>
                                    <Space direction="vertical" size={0}>
                                        <Typography.Text strong>Post title that can be quite big</Typography.Text>
                                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>Scheduled time</Typography.Text>
                                    </Space>
                                </Space>
                                <Space direction="vertical" style={{ width: "100%" }}>
                                    <div style={{ height: 12, width: "100%" }}>
                                        <p style={{fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>Производство металлических изделий на автоматизированных заводах — это новый шаг в эволюции промышленности. Интеграция современных технологий и роботизированных систем позволяет не только увеличить производительность, но и значительно повысить качество продукции. </p>
                                    </div>
                                </Space>
                                {/*<Row gutter={[8, 8]} style={{ marginTop: 12 }}>*/}
                                {/*    <Col span={8}>*/}
                                {/*        <div style={{ aspectRatio: "16/9", backgroundImage: "url(https://media.istockphoto.com/id/814423752/photo/eye-of-model-with-colorful-art-make-up-close-up.jpg?s=612x612&w=0&k=20&c=l15OdMWjgCKycMMShP8UK94ELVlEGvt7GmB_esHWPYE=)", borderRadius: 8 }} />*/}
                                {/*    </Col>*/}
                                {/*    <Col span={8}>*/}
                                {/*        <div style={{ aspectRatio: "16/9", backgroundImage: "url(https://media.istockphoto.com/id/814423752/photo/eye-of-model-with-colorful-art-make-up-close-up.jpg?s=612x612&w=0&k=20&c=l15OdMWjgCKycMMShP8UK94ELVlEGvt7GmB_esHWPYE=)", borderRadius: 8 }} />*/}
                                {/*    </Col>*/}
                                {/*    <Col span={8}>*/}
                                {/*        <div style={{ aspectRatio: "16/9", backgroundImage: "url(https://media.istockphoto.com/id/814423752/photo/eye-of-model-with-colorful-art-make-up-close-up.jpg?s=612x612&w=0&k=20&c=l15OdMWjgCKycMMShP8UK94ELVlEGvt7GmB_esHWPYE=)", borderRadius: 8 }} />*/}
                                {/*    </Col>*/}
                                {/*</Row>*/}
                            </Card>
                        </Card>

                        {/* Collaboration */}
                        <Card className="shadow-sm" style={{ marginTop: 16 }} bodyStyle={{ padding: 16 }}>
                            <Space align="center" size={8} style={{ marginBottom: 8 }}>
                                <UserAddOutlined />
                                <Title level={5} style={{ margin: 0 }}>Collaboration</Title>
                            </Space>
                            <Space direction="vertical" style={{ width: "100%" }}>
                                <Space align="center" size={8} style={{ color: "#8c8c8c" }}>
                                    <MessageOutlined /> Request review from teammates
                                </Space>
                                <Card size="small" bordered>
                                    <Text type="secondary">@Aizhan Can we add a CTA to sign up?</Text>
                                </Card>
                                <Card size="small" bordered>
                                    <Text type="secondary">@Almaz Maybe swap the order of images?</Text>
                                </Card>
                                <Space.Compact style={{ width: "100%" }}>
                                    <Input placeholder="Write a comment…" />
                                    <Button icon={<CommentOutlined />} type="primary">Comment</Button>
                                </Space.Compact>
                            </Space>
                        </Card>
                    </Col>
                </Row>
            </Content>

            {/* Bottom Bar */}
            <Footer style={{ position: "sticky", bottom: 0, zIndex: 9, background: "rgba(255,255,255,0.9)", backdropFilter: "blur(6px)", width: "100%", borderTop: "1px solid #f0f0f0", padding: "8px 0" }}>
                <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 16px" }}>
                    <Row align="middle" justify="space-between">
                        <Text type="secondary" style={{ fontSize: 12 }}>Draft saved a minute ago • Auto-save on</Text>
                        <Space>
                            <Button>Save as Draft</Button>
                            <Button type="default">Schedule</Button>
                            <Button type="primary" icon={<SendOutlined />}>Publish</Button>
                        </Space>
                    </Row>
                </div>
            </Footer>
        </Layout>
    );
}
